import React, { Fragment, useCallback, useReducer, useState } from "react";
import ReactModal from "react-modal";
import { toast } from "react-toastify";
import { useAuth0 } from "@auth0/auth0-react";
import NumberFormat from "react-number-format";
import Select from "react-select";
import moment from "moment-timezone";
import { Datepicker, momentTimezone } from "@mobiscroll/react";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import "@mobiscroll/react/dist/css/mobiscroll.min.css";

import ai from "../../helpers/axios";
import getApiErrorMessage from "../../helpers/get-api-error-message";
import formatPaymentType, { CLIENT_PAYMENT_TYPE_OPTIONS } from "../../helpers/format-payment-type";
import formatSignedAmount from "../../helpers/format-signed-amount";
import { getReferralFeeSignedAmount } from "../../helpers/referral-fees";
import shortenName from "../../helpers/shorten-name";
import { LoadingButton } from "../shared/loading-button";
import TextAreaInput from "../textarea-input";
import TextInput from "../text-input";
import Tooltip from "@mui/material/Tooltip";

const toastOptions = {
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

const TRANSACTION_TYPE_OPTIONS = [
  { label: "Received", value: "received" },
  { label: "Spent", value: "spent" },
];

const COLUMN_COUNT = 7;

const emptyForm = {
  itemId: null,
  paymentType: null,
  paymentDate: null,
  transactionType: TRANSACTION_TYPE_OPTIONS[1],
  amount: "",
  paidTo: "",
  notes: "",
};

const reducer = (state, action) => {
  switch (action.type) {
    case "fieldChanged":
      return { ...state, [action.field]: action.value };

    case "openModal":
      return { ...state, ...emptyForm, paymentDate: new Date(), showModal: true, ...(action.payload || {}) };

    case "openRemoveModal":
      return { ...state, showRemoveModal: true, itemId: action.record._id, removeRecord: action.record };

    case "closeModal":
      return { ...state, ...emptyForm, showModal: false, showRemoveModal: false, removeRecord: null };

    default:
      return state;
  }
};

const isLinked = (record) => !!(record.xeroTransactionId || record.stripeChargeId);

const ReferralFeeRow = ({ record, canEdit, canUnlink, onEdit, onRemove }) => {
  const [isOpen, setIsOpen] = useState(false);
  const signedAmount = getReferralFeeSignedAmount(record);
  const amount = formatSignedAmount(signedAmount);
  const linked = isLinked(record);
  const date = record.paymentDate || record.timestamp;

  return (
    <Fragment>
      <tr className="fm-l2-parent-table-row">
        <td>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setIsOpen((open) => !open)}
            className="p-0"
          >
            {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </td>
        <td>{date ? moment(date).format("M/D/YY") : "—"}</td>
        <td>{record.paymentType ? formatPaymentType(record.paymentType) : "—"}</td>
        <td>{shortenName(record?.enteredBy?.name)}</td>
        <td className="referral-fee-paid-to">
          <Tooltip
            classes={{
              tooltip: "multiline-tooltip",
            }}
            title={record.paidTo}
          >
            <div className="two-line-content">
              {record.paidTo}
            </div>
          </Tooltip>
        </td>
        <td className={`text-align-right ${amount.className}`}>{amount.text}</td>
        <td>
          {linked && canUnlink && (
            <div className="d-flex justify-content-end">
              <button
                className="base-button icon-button icon-button-small btn-danger"
                onClick={(event) => onRemove(event, record)}
                title="Unlink"
              >
                <span className="material-icons">link_off</span>
              </button>
            </div>
          )}
          {!linked && canEdit && (
            <div className="d-flex justify-content-end">
              <button
                className="base-button icon-button icon-button-small evo-blue"
                onClick={(event) => onEdit(event, record)}
                title="Edit"
              >
                <span className="material-icons">edit</span>
              </button>

              <button
                className="base-button icon-button icon-button-small btn-danger ml-2"
                onClick={(event) => onRemove(event, record)}
                title="Delete"
              >
                <span className="material-icons">delete</span>
              </button>
            </div>
          )}
        </td>
      </tr>
      <tr className={`payment-records-table-row-expandable ${isOpen ? "payment-records-table-row-expanded" : ""}`}>
        <td colSpan={COLUMN_COUNT}>
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            {record.notes || "No notes"}
          </Collapse>
        </td>
      </tr>
    </Fragment>
  );
};

const ReferralFeeModule = ({ tripId, data = [], parentDispatch, dataDownloadedTimestamp, readOnly, isAccountingAdmin }) => {
  const { getAccessTokenSilently } = useAuth0();

  const [state, dispatch] = useReducer(reducer, {
    ...emptyForm,
    showModal: false,
    showRemoveModal: false,
    removeRecord: null,
    saving: false,
  });

  const canEdit = !readOnly;
  const canUnlink = !readOnly && !!isAccountingAdmin;

  const setField = useCallback((field, value) => dispatch({ type: "fieldChanged", field, value }), []);

  const handleClose = useCallback(() => dispatch({ type: "closeModal" }), []);

  const refreshTrip = useCallback(() => {
    parentDispatch({ type: "fieldChanged", field: "refreshDataToken", value: Date.now() });
  }, [parentDispatch]);

  const handleAdd = useCallback((event) => {
    event.preventDefault();
    dispatch({ type: "openModal" });
  }, []);

  const handleEdit = useCallback((event, record) => {
    event.preventDefault();
    const signedAmount = getReferralFeeSignedAmount(record);

    dispatch({
      type: "openModal",
      payload: {
        itemId: record._id,
        paymentType: CLIENT_PAYMENT_TYPE_OPTIONS.find(({ value }) => value === record.paymentType) || null,
        paymentDate: record.paymentDate || record.timestamp || new Date(),
        transactionType: TRANSACTION_TYPE_OPTIONS[signedAmount < 0 ? 1 : 0],
        amount: Math.abs(signedAmount),
        paidTo: record.paidTo || "",
        notes: record.notes || "",
      },
    });
  }, []);

  const handleRemove = useCallback((event, record) => {
    event.preventDefault();
    dispatch({ type: "openRemoveModal", record });
  }, []);

  const withSaving = useCallback(
    async (request) => {
      setField("saving", true);

      try {
        const token = await getAccessTokenSilently();
        await request(ai.auth(token));
        handleClose();
        refreshTrip();
      } catch (error) {
        toast.error(getApiErrorMessage(error, "Failed to save referral fee."), toastOptions);
      } finally {
        setField("saving", false);
      }
    },
    [getAccessTokenSilently, handleClose, refreshTrip, setField]
  );

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();

      const amount = Math.abs(Number(state.amount));
      const paidTo = (state.paidTo || "").trim();

      if (!state.paymentType || !state.paymentDate || !state.transactionType || !amount || !paidTo) {
        return toast.error("Please fill in all required fields and retry.", toastOptions);
      }

      const payload = {
        paymentType: state.paymentType.value,
        paymentDate: state.paymentDate,
        amount: state.transactionType.value === "spent" ? -amount : amount,
        paidTo,
        notes: state.notes,
      };

      return withSaving((client) =>
        state.itemId
          ? client.put(`/api/trips/${tripId}/referral-fees/${state.itemId}`, payload)
          : client.post(`/api/trips/${tripId}/referral-fees`, payload)
      );
    },
    [state.amount, state.itemId, state.notes, state.paidTo, state.paymentDate, state.paymentType, state.transactionType, tripId, withSaving]
  );

  const handleRemoveConfirm = useCallback(
    (event) => {
      event.preventDefault();
      const record = state.removeRecord;

      if (record && isLinked(record)) {
        return withSaving((client) =>
          record.stripeChargeId
            ? client.post(`/api/stripe/transactions/${record.stripeChargeId}/unlink-referral-fee`, {})
            : client.post(`/api/xero/transactions/${record.xeroTransactionId}/unlink-referral-fee`, {
              lineItemID: record.lineItemID || null,
            })
        );
      }

      return withSaving((client) => client.delete(`/api/trips/${tripId}/referral-fees/${state.itemId}`));
    },
    [state.itemId, state.removeRecord, tripId, withSaving]
  );

  const isRemovingLinked = !!state.removeRecord && isLinked(state.removeRecord);

  return (
    <>
      <div
        className="trip-notes-module"
        key={`key-referral-fee-${dataDownloadedTimestamp.toString()}`}
        id="trip-referral-fee"
      >
        <h5 className="mb-1 d-flex">
          Referral Fees

          {canEdit && (
            <button
              className="base-button icon-button icon-button-small evo-dark-green ml-2"
              onClick={handleAdd}
              title="Add"
            >
              <span className="material-icons">add_circle</span>
            </button>
          )}
        </h5>

        <div className="trip-notes-table trip-referral-fee-table pb-0">
          <table>
            <thead className="fm-l2-parent-table-head">
              <tr className="fm-l2-parent-table-head-row">
                <th />
                <th>Date</th>
                <th>Payment Type</th>
                <th>Entered By</th>
                <th>Paid To</th>
                <th className="text-align-right">Amount</th>
                <th className="text-align-right">Actions</th>
              </tr>
            </thead>
            <tbody className="fm-l2-parent-table-body">
              {!data.length && (
                <tr className="fm-l2-parent-table-row">
                  <td className="text-align-left" colSpan={COLUMN_COUNT}>
                    No referral fees have been added
                  </td>
                </tr>
              )}
              {data.map((record) => (
                <ReferralFeeRow
                  key={record._id}
                  record={record}
                  canEdit={canEdit}
                  canUnlink={canUnlink}
                  onEdit={handleEdit}
                  onRemove={handleRemove}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ReactModal
        isOpen={state.showModal}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleClose}
        className="trip-notes-module-modal"
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            border: "none",
            maxWidth: "600px",
            maxHeight: "80vh",
            minWidth: "calc(min(600px, 95vw))",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header edit-note-modal">
          <h4>{state.itemId ? "Edit" : "Add"} Referral Fee</h4>
        </div>
        <div className="modal-body edit-note-modal">
          <form onSubmit={handleSubmit} className="w-100 base-form mt-20">
            <div className="w-100 d-flex gap-20">
              <div className="form-group flex-half">
                <label htmlFor="paymentType" className="base-label">
                  Payment Type <span className="required">*</span>
                </label>

                <Select
                  classNamePrefix="select"
                  options={CLIENT_PAYMENT_TYPE_OPTIONS}
                  placeholder="Payment Type"
                  value={state.paymentType}
                  onChange={(option) => setField("paymentType", option)}
                  name="paymentType"
                  menuPosition="fixed"
                />
              </div>

              <div className="datepickerContainer mb-30 flex-half">
                <label htmlFor="paymentDate" className="base-label">
                  Date <span className="required">*</span>
                </label>

                <Datepicker
                  value={state.paymentDate}
                  onChange={(event) => setField("paymentDate", event.value)}
                  dataTimezone="utc"
                  displayTimezone="local"
                  timezonePlugin={momentTimezone}
                  controls={["date"]}
                />
              </div>
            </div>

            <div className="w-100 d-flex gap-20">
              <div className="form-group flex-half">
                <label htmlFor="transactionType" className="base-label">
                  Transaction Type <span className="required">*</span>
                </label>

                <Select
                  className="transactionType-select"
                  classNamePrefix="select"
                  options={TRANSACTION_TYPE_OPTIONS}
                  value={state.transactionType}
                  onChange={(option) => setField("transactionType", option)}
                  name="transactionType"
                  menuPosition="fixed"
                />
              </div>

              <div className="form-group flex-half">
                <label htmlFor="amount" className="base-label">
                  Amount <span className="required">*</span>
                </label>

                <NumberFormat
                  value={state.amount}
                  thousandSeparator={true}
                  prefix={state.transactionType?.value === "spent" ? "-$" : "$"}
                  onValueChange={({ value }) => setField("amount", value)}
                  placeholder="Amount"
                  className={`semibold-text amount-${state.transactionType?.value === "spent" ? "negative" : "positive"}`}
                  decimalScale={2}
                  name="amount"
                  allowNegative={false}
                />
              </div>
            </div>

            <TextInput
              name="paidTo"
              label="Paid To"
              handleChange={(event) => setField("paidTo", event.target.value)}
              value={state.paidTo}
              type="text"
              required
            />

            <TextAreaInput
              label="Notes"
              name="notes"
              value={state.notes}
              handleChange={(event) => setField("notes", event.target.value)}
            />

            <div className="w-100 mt-20 d-flex gap-20">
              <LoadingButton
                className="base-button evo-blue flex-half"
                type="submit"
                isLoading={state.saving}
              >
                Submit
              </LoadingButton>

              <button
                className="base-button btn-danger flex-half"
                type="button"
                onClick={handleClose}
                disabled={state.saving}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </ReactModal>

      <ReactModal
        isOpen={state.showRemoveModal}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleClose}
        className="trip-notes-module-modal"
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            padding: "40px",
            border: "none",
            maxWidth: "700px",
            maxHeight: "50%",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h4>{isRemovingLinked ? "Unlink" : "Delete"} Referral Fee</h4>
        </div>

        <div className="modal-body base-modal overflow-scroll">
          <div className="w-100 mt-20">
            {isRemovingLinked
              ? "This referral fee will be unlinked from its transaction and removed from the trip. Are you sure you want to continue?"
              : "This referral fee will be permanently deleted from the trip. Are you sure you want to continue?"}
          </div>

          <div className="w-100 mt-20 d-flex gap-20">
            <button
              className="base-button evo-blue flex-half"
              type="button"
              onClick={handleClose}
              disabled={state.saving}
            >
              Cancel
            </button>

            <LoadingButton
              className="base-button btn-danger flex-half"
              type="button"
              onClick={handleRemoveConfirm}
              isLoading={state.saving}
            >
              {isRemovingLinked ? "Unlink" : "Delete"}
            </LoadingButton>
          </div>
        </div>
      </ReactModal>
    </>
  );
};

export default ReferralFeeModule;
